import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useGameStore } from '@/store/gameStore'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { CLUB_LEVELS, getNextLevel } from '@/config/clubLevelConfig'
import { getClubHero } from '@/config/campusConfig'
import { DIVISIONS, PLAYER_CLUB_ID, SEASON, computeStandings } from '@/config/leagueConfig'
import { getTotalEstimatedIncome } from '@/game/systems/DemandSystem'
import { getWeeklyDepreciation } from '@/config/financeConfig'
import { useTopModal } from '../modals/useTopModal'
import { SPRING_MODAL } from '../motion/motionTokens'
import { AngledButton, MatchResultCard } from './broadcast'
import { ModalCloseButton } from './ModalCloseButton'
import { FileText, Star, Target, Shield } from 'lucide-react'

const eur = (n: number) => `${n.toLocaleString('es-ES')}€`

interface LeagueReport {
  divisionName: string
  position: number
  clubs: number
  matchday: number
  total: number
  lastResult: {
    matchday: number
    homeName: string
    awayName: string
    homeScore: number
    awayScore: number
    won: boolean
    isHome: boolean
  } | null
  nextMatch: { matchday: number; opponent: string; isHome: boolean } | null
}

interface ReportData {
  week: number
  income: number
  opex: number          // operating expenses (maintenance, salaries, loans…)
  capex: number         // capital outflow (obras / construction) — cash, not P&L
  depreciation: number  // non-cash weekly amortización of courts + buildings
  courts: number
  staff: number
  members: number
  reputation: number
  clubLevel: number
  services: number
  activeEffects: number
  league: LeagueReport | null
}

export function ReportModal() {
  const [report, setReport] = useState<ReportData | null>(null)
  const [dismissed, setDismissed] = useState<number>(0) // last dismissed week
  const [view, setView] = useState<'cash' | 'pyg'>('cash') // cash flow vs P&L
  const top = useTopModal() // wait until no higher-priority modal is open

  useEffect(() => {
    const handleWeekChanged = () => {
      const store = useGameStore.getState()

      // Don't show on week 1 or if already dismissed this week
      if (store.week <= 1) return

      // Calculate weekly income/expenses from transactions
      const weekStart = Math.max(1, store.day - 7)
      const weekTransactions = store.transactions.filter(t => t.day >= weekStart)
      const income = weekTransactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0)
      // Split expenses: operating (opex) vs capital outflows / obras (capex).
      const expenseTx = weekTransactions.filter(t => t.type === 'expense')
      const capex = expenseTx.filter(t => t.capex).reduce((s, t) => s + t.amount, 0)
      const opex = expenseTx.filter(t => !t.capex).reduce((s, t) => s + t.amount, 0)
      // Non-cash: current weekly straight-line amortización of the assets.
      const depreciation = getWeeklyDepreciation(store.courts, store.buildings)
      const totalMembers = Object.values(store.memberCounts).reduce((s, c) => s + c, 0)

      // League snapshot for the weekly report (if enrolled)
      let league: LeagueReport | null = null
      if (store.league) {
        const standings = computeStandings(store.league, 'Mi Club')
        const position = standings.findIndex(r => r.clubId === PLAYER_CLUB_ID) + 1
        const clubName = (id: string) =>
          id === PLAYER_CLUB_ID ? 'Mi Club' : store.league!.clubs.find(c => c.id === id)?.name ?? id
        const played = store.league.fixtures
          .filter(f => f.result && (f.homeId === PLAYER_CLUB_ID || f.awayId === PLAYER_CLUB_ID))
          .sort((a, b) => b.matchday - a.matchday)
        const last = played[0]
        const upcoming = store.league.fixtures
          .filter(f => !f.result && (f.homeId === PLAYER_CLUB_ID || f.awayId === PLAYER_CLUB_ID))
          .sort((a, b) => a.matchday - b.matchday)
        const next = upcoming[0]
        league = {
          divisionName: DIVISIONS[store.league.division].name,
          position,
          clubs: standings.length,
          matchday: Math.min(SEASON.matchdays, Math.max(0, store.week - store.league.seasonStartWeek)),
          total: SEASON.matchdays,
          lastResult: last
            ? {
                matchday: last.matchday,
                homeName: clubName(last.homeId),
                awayName: clubName(last.awayId),
                homeScore: last.result!.homeMatchWins,
                awayScore: last.result!.awayMatchWins,
                won: last.result!.winnerId === PLAYER_CLUB_ID,
                isHome: last.homeId === PLAYER_CLUB_ID,
              }
            : null,
          nextMatch: next
            ? {
                matchday: next.matchday,
                opponent: clubName(next.homeId === PLAYER_CLUB_ID ? next.awayId : next.homeId),
                isHome: next.homeId === PLAYER_CLUB_ID,
              }
            : null,
        }
      }

      setReport({
        week: store.week - 1, // Report for previous week
        income: Math.round(income),
        opex: Math.round(opex),
        capex: Math.round(capex),
        depreciation: Math.round(depreciation),
        courts: store.courts.length,
        staff: store.staff.length,
        members: totalMembers,
        reputation: store.reputation,
        clubLevel: store.clubLevel,
        services: store.enabledServices.length,
        activeEffects: store.activeEffects.length,
        league,
      })
    }

    EventBus.on(GameEvents.WEEK_CHANGED, handleWeekChanged)
    return () => { EventBus.off(GameEvents.WEEK_CHANGED, handleWeekChanged) }
  }, [])

  // Lowest priority: hold the report until every higher-priority modal is gone
  // (construction reveal, level-up, story beat, event, game over/victory).
  if (!report || report.week === dismissed || top !== null) return null

  const levelInfo = CLUB_LEVELS.find(l => l.level === report.clubLevel)
  const nextLevel = getNextLevel(report.clubLevel)
  const estimatedDaily = Math.round(getTotalEstimatedIncome())
  const cashTotal = report.income - report.opex - report.capex
  const pygTotal = report.income - report.opex - report.depreciation
  const total = view === 'cash' ? cashTotal : pygTotal

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="modal-backdrop"
        style={{ zIndex: 190 }}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.92, x: '-50%', y: '-50%' }}
        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%', transition: SPRING_MODAL }}
        className="modal-card pointer-events-auto"
        style={{
          zIndex: 191,
          width: 'min(440px, 92vw)',
          overflow: 'hidden auto',
        }}
      >
        <ModalCloseButton onClick={() => setDismissed(report.week)} />
        {/* Header band over the club hero */}
        <div style={{ position: 'relative', minHeight: '92px' }}>
          <img
            src={getClubHero(report.clubLevel).image}
            alt=""
            aria-hidden
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center 65%',
            }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <div style={{
            position: 'relative',
            background: 'linear-gradient(90deg, rgba(13,21,32,0.92) 30%, rgba(13,21,32,0.55) 100%)',
            padding: '1rem 1.25rem',
            minHeight: '92px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              fontSize: '0.7rem', color: 'var(--gold)',
              letterSpacing: '2.5px', textTransform: 'uppercase', fontWeight: 700,
            }}>
              <FileText size={12} /> Informe semanal
            </div>
            <div style={{
              fontFamily: 'var(--font-titles)', fontWeight: 800,
              fontSize: '1.35rem', color: '#fff',
            }}>
              Semana {report.week}
            </div>
            {levelInfo && (
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)' }}>
                {levelInfo.name}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '1.1rem 1.25rem 1.25rem' }}>
          {/* Cash flow vs P&L (Cuenta de Resultados) */}
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem' }}>
            <FinToggle active={view === 'cash'} onClick={() => setView('cash')} label="Flujo de caja" />
            <FinToggle active={view === 'pyg'} onClick={() => setView('pyg')} label="Resultado (PyG)" />
          </div>

          <div style={{
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--fill-softer)',
            padding: '0.55rem 0.75rem',
            marginBottom: '1rem',
            fontVariantNumeric: 'tabular-nums',
          }}>
            <FinLine label="Ingresos" amount={report.income} sign="+" tone="success" />
            <FinLine label="Gastos operativos" amount={report.opex} sign="−" tone="danger" />
            {view === 'cash'
              ? <FinLine label="Inversión (obras)" amount={report.capex} sign="−" tone="danger" />
              : <FinLine label="Amortización" amount={report.depreciation} sign="−" tone="danger" />}
            <div style={{ height: '1px', background: 'var(--border-default)', margin: '0.4rem 0' }} />
            <FinLine
              label={view === 'cash' ? 'Flujo de caja' : 'Resultado'}
              amount={Math.abs(total)}
              sign={total >= 0 ? '+' : '−'}
              tone={total >= 0 ? 'success' : 'danger'}
              strong
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.45rem', lineHeight: 1.4 }}>
              {view === 'cash'
                ? 'Dinero que entra y sale, incluida la inversión en obras.'
                : 'Excluye la inversión y reparte el coste de los activos como amortización (gasto no monetario).'}
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.35rem 0.5rem',
            marginBottom: '1rem',
            fontSize: '0.8125rem',
          }}>
            <StatRow label="Pistas" value={report.courts} />
            <StatRow label="Personal" value={report.staff} />
            <StatRow label="Socios" value={report.members} />
            <StatRow label="Servicios" value={report.services} />
            <StatRow
              label="Reputacion"
              value={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                  <Star size={11} style={{ color: 'var(--warning)' }} fill="currentColor" />
                  {report.reputation.toFixed(1)}
                </span>
              }
            />
            <StatRow label="Ingresos/dia est." value={eur(estimatedDaily)} />
          </div>

          {/* League block (when enrolled) */}
          {report.league && (
            <div style={{
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              padding: '0.6rem',
              marginBottom: '1rem',
              background: 'var(--fill-softer)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem',
              }}>
                <Shield size={13} style={{ color: 'var(--gold)' }} />
                <span style={{ fontWeight: 700, fontSize: '0.8125rem', color: 'var(--text-strong)' }}>
                  {report.league.divisionName}
                </span>
                <span className="chip-angled tone-gold" style={{ marginLeft: 'auto' }}>
                  {report.league.position}º / {report.league.clubs}
                </span>
              </div>
              {report.league.lastResult ? (
                <MatchResultCard {...report.league.lastResult} />
              ) : (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Jornada {report.league.matchday}/{report.league.total} · aún sin partidos disputados
                </div>
              )}
              {report.league.nextMatch && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  marginTop: '0.5rem', paddingTop: '0.5rem',
                  borderTop: '1px solid var(--border-default)',
                  fontSize: '0.75rem', color: 'var(--text-secondary)',
                }}>
                  <Target size={12} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <span>
                    Próxima · J{report.league.nextMatch.matchday}{' '}
                    {report.league.nextMatch.isHome ? 'vs' : '@'}{' '}
                    <strong style={{ color: 'var(--text-strong)' }}>{report.league.nextMatch.opponent}</strong>
                  </span>
                  <span className="pill" style={{ marginLeft: 'auto' }}>
                    {report.league.nextMatch.isHome ? 'Local' : 'Visitante'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Next level hint */}
          {nextLevel && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              fontSize: '0.78rem', color: 'var(--text-secondary)',
              background: 'var(--fill-softer)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              padding: '0.45rem 0.6rem',
              marginBottom: '1rem',
            }}>
              <Target size={13} style={{ color: 'var(--cta)', flexShrink: 0 }} />
              <span>Proximo objetivo: <strong style={{ color: 'var(--text-strong)' }}>{nextLevel.name}</strong></span>
              {report.activeEffects > 0 && (
                <span className="pill pill-info" style={{ marginLeft: 'auto' }}>
                  {report.activeEffects} efectos
                </span>
              )}
            </div>
          )}

          <AngledButton
            tone="gold"
            block
            onClick={() => { setDismissed(report.week) }}
          >
            Continuar
          </AngledButton>
        </div>
      </motion.div>
    </>
  )
}

function FinToggle({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, cursor: 'pointer', border: 'none', borderRadius: '8px',
        padding: '0.45rem 0.5rem', fontSize: '0.78rem', fontWeight: 700,
        background: active ? 'var(--gold-fill)' : 'var(--fill-soft)',
        color: active ? 'var(--gold)' : 'var(--text-secondary)',
      }}
    >
      {label}
    </button>
  )
}

function FinLine({ label, amount, sign, tone, strong }: {
  label: string
  amount: number
  sign: string
  tone: 'success' | 'danger'
  strong?: boolean
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.18rem 0' }}>
      <span style={{
        color: strong ? 'var(--text-strong)' : 'var(--text-secondary)',
        fontSize: strong ? '0.86rem' : '0.82rem', fontWeight: strong ? 700 : 500,
      }}>
        {label}
      </span>
      <span style={{ color: `var(--${tone})`, fontWeight: strong ? 800 : 700, fontSize: strong ? '0.95rem' : '0.85rem' }}>
        {sign}{amount.toLocaleString('es-ES')}€
      </span>
    </div>
  )
}

function StatRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.25rem 0.5rem',
      background: 'var(--fill-softer)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ color: 'var(--text-strong)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  )
}
